/**
 * Gamme d'entretien d'une machine, sur sa fiche (CDC §8.2, §8.3).
 *
 * Remplace la maquette `GammeApercu` : les données viennent désormais de la base.
 *
 * DIFFÉRENCE ASSUMÉE avec la maquette : elle s'affichait toute seule quand la
 * marque et le modèle correspondaient. Une vraie gamme ne se rattache JAMAIS
 * d'elle-même (« le produit peut suggérer un profil voisin, jamais l'imposer ») :
 * la carte PROPOSE, et c'est un geste explicite — audité, qu'on peut défaire —
 * qui rattache.
 *
 * Sans le droit de lire les prix, l'API n'en rend aucun : l'écran n'a pas à les
 * masquer, il n'a simplement rien à montrer.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useDroitsGammes } from '../auth/useDroits';
import { ApiError, api, type AvertissementReferenceGamme, type MaintenanceMachine } from '../api/client';
import { niveauxPourChoix } from '../domaine/entretiens-realises';
import { dateFr, decrireAvertissementReference } from '../domaine/gammes';
import { messageGammeCopiee } from '../domaine/gammes-identite';
import { ContenuGamme } from './ContenuGamme';
import { FormulaireGamme, type CopieGamme } from './FormulaireGamme';
import { ProchainEntretien } from './ProchainEntretien';

/**
 * L'avertissement de variante : orange quand la référence de la machine DIFFÈRE de celle de la
 * gamme, bleu (simple information) quand la machine n'en renseigne pas. Il n'empêche rien.
 */
function AvertissementReference({
  avertissement,
  dejaRattachee = false,
}: {
  avertissement: AvertissementReferenceGamme | null;
  dejaRattachee?: boolean;
}) {
  if (!avertissement) return null;
  return (
    <div className={`message ${avertissement.type === 'DIFFERENTE' ? 'avertissement' : 'info'}`}>
      {decrireAvertissementReference(avertissement, { dejaRattachee })}
    </div>
  );
}

export function GammeEntretien({
  assetId,
  modele,
  referenceConstructeur,
  cleEntretien = 0,
  onEntretienEcrit,
}: {
  assetId: string;
  /** Modèle et référence constructeur de la machine : ils préremplissent une gamme créée pour elle. */
  modele: string | null;
  referenceConstructeur: string | null;
  /** Change quand les compteurs de la fiche changent : la carte « Prochain entretien » se recalcule. */
  cleEntretien?: number;
  /** Appelé après l'écriture d'un entretien : la fiche rafraîchit les compteurs (un relevé a pu être écrit). */
  onEntretienEcrit?: () => void;
}) {
  const droits = useDroitsGammes();
  const naviguer = useNavigate();
  const [vue, setVue] = useState<MaintenanceMachine | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  /** Une gamme proposée dont on part pour en créer une nouvelle, adaptée à cette machine. */
  const [copie, setCopie] = useState<CopieGamme | null>(null);

  const charger = useCallback(async () => {
    try {
      setVue(await api.assetMaintenance(assetId));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement de la gamme impossible.');
    }
  }, [assetId]);

  useEffect(() => {
    setVue(null);
    void charger();
  }, [charger]);

  const rattacher = async (profileId: string, avertissement: AvertissementReferenceGamme | null) => {
    // Une autre variante : on demande confirmation. Rien n'est refusé — l'utilisateur sait peut-être
    // que les pièces sont les mêmes (le BSD75 en 101253.2 a les pièces du 101253.1).
    if (avertissement?.type === 'DIFFERENTE') {
      const ok = window.confirm(
        `${decrireAvertissementReference(avertissement)}\n\nRattacher quand même cette gamme à cette machine ?`,
      );
      if (!ok) return;
    }
    setEnCours(true);
    try {
      await api.assignMaintenance(assetId, profileId);
      await charger();
    } catch (e) {
      // L'erreur est posée APRÈS : `charger()` la remet à zéro en cas de succès.
      setErreur(e instanceof ApiError ? e.message : 'Rattachement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  /**
   * Prépare la création d'une NOUVELLE gamme à partir d'une gamme proposée : on lit la gamme et sa
   * version en vigueur, puis on ouvre le formulaire de copie, déjà rempli avec le modèle et la
   * référence constructeur de CETTE machine — la nouvelle gamme lui correspondra tout de suite.
   */
  const preparerCopie = async (profileId: string, versionNumber: number) => {
    setEnCours(true);
    setErreur(null);
    try {
      const detail = await api.maintenanceProfile(profileId);
      const version =
        detail.versions.find((v) => v.statut === 'PUBLIEE' && v.versionNumber === versionNumber) ??
        detail.versions.find((v) => v.statut === 'PUBLIEE');
      if (!version) {
        setErreur('Cette gamme n’a aucune version publiée à copier.');
        return;
      }
      setCopie({
        depuis: detail,
        version,
        ...(modele ? { criteres: [{ modele, reference: referenceConstructeur ?? '' }] } : {}),
        ...(referenceConstructeur ? { references: [referenceConstructeur] } : {}),
      });
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Lecture de la gamme impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const detacher = async (code: string) => {
    const ok = window.confirm(
      `Détacher la gamme « ${code} » de cette machine ?\n\n` +
        'La gamme reste disponible pour les autres machines ; l’historique du rattachement est conservé.',
    );
    if (!ok) return;
    setEnCours(true);
    try {
      await api.unassignMaintenance(assetId);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Détachement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const rattachement = vue?.rattachement ?? null;

  return (
    <div className="carte">
      <h2>Gamme d’entretien</h2>

      {erreur && <div className="message erreur">{erreur}</div>}

      <div className="carte-corps">
        {!vue && !erreur && <div className="vide">Chargement…</div>}

        {rattachement && (
          <>
            <div className="saisie-ligne">
              <span>
                <strong>{rattachement.profile.label}</strong>{' '}
                <span className="aide">
                  ({rattachement.profile.code}
                  {rattachement.version
                    ? ` · version ${rattachement.version.versionNumber}, en vigueur depuis le ${dateFr(rattachement.version.validFrom)}`
                    : ''}
                  )
                </span>
              </span>
              <button
                className="secondaire discret danger-discret"
                disabled={enCours}
                onClick={() => void detacher(rattachement.profile.code)}
              >
                Détacher
              </button>
            </div>

            {rattachement.version && (
              <p className="aide">
                <Link
                  to={`/gammes/imprimer?gamme=${rattachement.profile.id}&version=${rattachement.version.id}&machine=${assetId}`}
                >
                  Imprimer ou exporter cette gamme
                </Link>
              </p>
            )}

            <AvertissementReference avertissement={rattachement.avertissementReference} dejaRattachee />

            {/* Le prochain entretien se lit AVANT le contenu de la gamme : c'est ce que l'on vient chercher. */}
            <ProchainEntretien
              assetId={assetId}
              cle={cleEntretien}
              niveaux={niveauxPourChoix(rattachement.version?.contenu.levels ?? [])}
              {...(onEntretienEcrit ? { onEntretienEcrit } : {})}
            />

            {rattachement.version ? (
              <ContenuGamme version={rattachement.version} />
            ) : (
              <div className="message info">
                Cette gamme n’a plus de version publiée en vigueur : ses lignes ne s’affichent pas.
              </div>
            )}
          </>
        )}

        {vue && !rattachement && vue.suggestions.length > 0 && (
          <>
            <div className="message info">
              {vue.suggestions.length === 1 ? 'Une gamme correspond' : 'Plusieurs gammes correspondent'} à
              cette machine (même marque, même modèle). C’est une proposition : rien n’est rattaché
              tant que vous ne le décidez pas.
            </div>
            <ul className="liste-regles-rattachees">
              {vue.suggestions.map((s) => (
                <li key={s.profileId} className={s.avertissementReference ? 'avec-avertissement' : undefined}>
                  <span>
                    <strong>{s.label}</strong>{' '}
                    <span className="aide">
                      ({s.code} · version {s.versionNumber})
                    </span>
                  </span>
                  <button
                    className="secondaire discret"
                    disabled={enCours}
                    onClick={() => void rattacher(s.profileId, s.avertissementReference)}
                  >
                    Rattacher
                  </button>
                  {droits.peutGerer && (
                    <button
                      className="secondaire discret"
                      disabled={enCours}
                      title="Crée une nouvelle gamme qui reprend le contenu et les prix de celle-ci, à modifier avant de la publier"
                      onClick={() => void preparerCopie(s.profileId, s.versionNumber)}
                    >
                      Modifier pour en créer une nouvelle
                    </button>
                  )}
                  <AvertissementReference avertissement={s.avertissementReference} />
                </li>
              ))}
            </ul>
          </>
        )}

        {vue && !rattachement && vue.suggestions.length === 0 && vue.raison && (
          <div className="vide">
            {vue.raison} <Link to="/parc/a-valider">Machines à valider</Link>
          </div>
        )}

        {vue && !rattachement && vue.suggestions.length === 0 && !vue.raison && (
          <div className="vide">
            Aucune gamme d’entretien rattachée à cette machine, et aucune ne correspond à sa marque et
            son modèle. <Link to="/gammes">Voir les gammes</Link>
          </div>
        )}
      </div>

      {copie && (
        <FormulaireGamme
          copie={copie}
          onFermer={() => setCopie(null)}
          onEnregistree={(nouvelle) => {
            setCopie(null);
            // La nouvelle gamme est un brouillon : elle ne peut recevoir une machine qu'une fois publiée.
            // On emmène l'utilisateur sur elle, pour la modifier puis la publier.
            naviguer(`/gammes?gamme=${nouvelle.id}`, {
              state: { message: messageGammeCopiee(nouvelle.label, copie.depuis.label) },
            });
          }}
        />
      )}
    </div>
  );
}
